import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { UserService } from '../../../../services/user-service/userservice';
import { OrganisationService } from '../../../../services/dataServices/organisation-service/organisationservice';
import { UtilService } from '../../../../services/util-service/utilService';
import { DialogService } from '../../../../services/dialog-service/dialog-service.service';
import { OrganisationInterface } from '../../../../model/organisations/organisation-interface';
import { UserInterface } from '../../../../model/user/user-interface';
import { Subscription } from 'rxjs';
import { HeaderServiceService } from 'src/app/services/header-service/header-service.service';
import { OrganisationMinimalInterface } from 'src/app/model/organisations/organisation-minimal-interface';

@Component({
    selector: 'app-organisation-screen',
    templateUrl: './organisation-screen.component.html',
    styleUrls: ['./organisation-screen.component.scss'],
    standalone: false
})
export class OrganisationScreenComponent implements OnInit, OnDestroy {
    addOrganisationMode: boolean = false;
    newOrganisationMode: boolean = false;

    newOrganisationAddress: string;
    newOrganisationNumber: string;

    // array of organisations for DropDown-Menu
    organisations: OrganisationMinimalInterface[] = [];

    private addOrganisation: OrganisationInterface;
    public newOrganisation: OrganisationInterface;
    private currentMembershipSubscription: Subscription;
    public isLoading: boolean = false;

    constructor(
        public userService: UserService,
        private organisationService: OrganisationService,
        private dialogService: DialogService,
        private utilService: UtilService,
        private cd: ChangeDetectorRef,
        private titleService: Title,
        public headerService: HeaderServiceService,
        private route: ActivatedRoute
    ) {
        document.documentElement.style.setProperty('--scrollStatus', 'auto');
    }

    ngOnInit(): void {
        this.titleService.setTitle('Mitgliedschaften - Construct-X');
        this.addOrganisation = {
            membershipStatus: 'ADMIN_CONFIRMATION_PENDING',
            userId: this.userService.currentUser.userId,
            organisationId: null,
            organisationName: null,
            organisationType: [],
            address: null,
            domain: null,
            role: null,
            website: null
        };

        this.newOrganisation = {
            address: {
                city: null,
                postalCode: null,
                street: '',
                streetAdditional: null
            },
            domain: '',
            organisationId: null,
            organisationName: null,
            organisationType: [],
            description: '',
            website: null
        };

        this.currentMembershipSubscription =
            this.userService.currentMembershipsObserver.subscribe((x) => {
                this.fillOrganisationlist();
                this.cd.detectChanges();
            });

        const mode = this.route.snapshot.queryParamMap.get('mode');
        if (mode === 'join') {
            this.onAddOrganisationToggle();
            this.fillOrganisationlist();
        } else if (mode === 'create') {
            this.newOrganisationModeToggle();
        }
    }

    ngOnDestroy() {
        document.documentElement.style.setProperty('--scrollStatus', 'hidden');

        this.currentMembershipSubscription.unsubscribe();
    }

    // fetch all organisations from organisationService to fill up organisation array
    // sollte nach der Überarbeitung der Orga-DTOs sich nur noch die Namen der Orgs holen
    fillOrganisationlist(): void {
        this.organisationService.getMinimalOrganisationList().subscribe((organisations) => {
            this.organisations = organisations;
        });
    }

    onAddOrganisationToggle(): void {
        this.addOrganisationMode = !this.addOrganisationMode;
        this.newOrganisationMode = false;
    }

    newOrganisationModeToggle(): void {
        this.newOrganisationMode = !this.newOrganisationMode;
        this.addOrganisationMode = false;
    }

    removeOrganisationMembership(id: number, role: string): void {
        this.dialogService
            .openConfirmDialog(
                'Wollen Sie die Mitgliedschaft wirklich entfernen?',
                'Abbrechen',
                'Entfernen'
            )
            .subscribe((result) => {
                if (result) {
                    this.isLoading = true;
                    this.cd.detectChanges();
                    this.userService.removeOrganisationMembership(id).subscribe(
                        (res) => {
                            this.userService.fetchCurrentUser();
                            this.isLoading = false;
                            this.cd.detectChanges();
                            this.dialogService.openDialog(
                                'Mitgliedschaft wurden erfolgreich entfernt',
                                ''
                            );
                        },
                        (error) => { 
                            this.isLoading = false;
                            this.cd.detectChanges();
                        }
                    );
                }
            });
    }

    isUserPlatformAdmin(): boolean {
        return this.userService.isCurrentUserPlatformAdmin();
    }

    acceptOrganisationMembership(id: number): void {
        this.userService.acceptOrganisationMembership(id).subscribe(
            (res: UserInterface) => {
                this.userService.saveUser(res);
                this.userService.getUserMemberships();
            },
            (error) => { }
        );
    }

    addOrganisationMembership(): void {
        if (
            !this.addOrganisation.organisationName ||
            !this.addOrganisation.role
        ) {
            this.dialogService.openDialog(
                'Antrag auf Mitgliedschaft',
                'Bitte geben Sie die Organisation und Rolle an!'
            );
            return;
        }
        this.addOrganisation.organisationId = this.organisations.find(
            organisation => organisation.name === this.addOrganisation.organisationName
        )?.id;
        if (!this.addOrganisation.organisationId) {
            this.dialogService.openDialog(
                'Antrag auf Mitgliedschaft',
                'Die ausgewählte Organisation ist nicht mehr verfügbar.'
            );
            return;
        }

        const existingPendingMembership = this.userService.currentMemberships.find(
            (membership) =>
                membership.organisationId === this.addOrganisation.organisationId &&
                membership.membershipStatus === 'ADMIN_CONFIRMATION_PENDING'
        );

        if (existingPendingMembership) {
            this.dialogService.openDialog(
                'Antrag bereits gestellt',
                `Für die Organisation "${this.addOrganisation.organisationName}" liegt bereits eine ausstehende Mitgliedschaftsanfrage von Ihnen vor.`
            );
            return;
        }
        this.userService.addOrganisationMembership(
            this.addOrganisation,
            (e) => { }
        );
    }

    removeMembershipRequest(organisationId: number): void {
        this.userService.removeOrganisationMembership(organisationId).subscribe(
            (res) => {
                this.userService.fetchCurrentUser();
            },
            (error) => { }
        );
    }

    openImportDialog(): void {
        this.dialogService.openOrganisationImportDialog().subscribe((result) => {
            if (result) {
                // Refresh list if needed
                this.userService.fetchCurrentUser();
            }
        });
    }

    /**
     * Toggles the "Add Organisation"- Form off and resets the drop down list to placeholder values.
     */
    onCancelAddOrganisation(): void {
        this.onAddOrganisationToggle();
        this.addOrganisation.role = null;
        this.addOrganisation.organisationName = null;
    }
}
